import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from '../../styles/StyledComponents';
import { Link as ScrollLink } from 'react-scroll';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${({ scrolled, theme }) =>
    scrolled ? theme.colors.white : 'transparent'};
  box-shadow: ${({ scrolled, theme }) =>
    scrolled ? theme.shadows.small : 'none'};
  transition: all ${({ theme }) => theme.transitions.standard};
  padding: ${({ scrolled }) => (scrolled ? '0.75rem 0' : '1.5rem 0')};
`;

const NavContainer = styled(Container)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  cursor: pointer;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-family: 'Exo 2', sans-serif;
  transition: color 0.3s ease;
  
  .tech {
    font-weight: 700;
    color: ${({ scrolled, theme }) => 
      scrolled ? theme.colors.secondary : theme.colors.white};
  }
  
  .ramp {
    color: ${({ scrolled, theme }) => 
      scrolled ? theme.colors.secondary : theme.colors.white};
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(ScrollLink)`
  margin-left: 2rem;
  font-weight: 500;
  color: ${({ scrolled, theme }) =>
    scrolled ? theme.colors.secondary : theme.colors.white};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width ${({ theme }) => theme.transitions.standard};
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
    color: ${({ scrolled, theme }) =>
      scrolled ? theme.colors.secondary : theme.colors.white};
    font-size: 1.5rem;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 70%;
  max-width: 300px;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  padding: 2rem;
  z-index: 1000;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
`;

const MobileLogoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const MobileLogoText = styled.span`
  font-size: 1.5rem;
  font-family: 'Exo 2', sans-serif;
  
  .tech {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  .ramp {
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const MobileNavLink = styled(ScrollLink)`
  margin: 1rem 0;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 2rem;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  return (
    <HeaderContainer scrolled={scrolled}>
      <NavContainer>
        <ScrollLink to="hero" smooth={true} duration={500}>
          <Logo>
            <LogoText scrolled={scrolled}>
              <span className="tech">Tech</span>
              <span className="ramp">Ramp</span>
            </LogoText>
          </Logo>
        </ScrollLink>
        
        <NavLinks>
          <NavLink to="process" smooth={true} duration={500} scrolled={scrolled}>How It Works</NavLink>
          <NavLink to="testimonials" smooth={true} duration={500} scrolled={scrolled}>Testimonials</NavLink>
          <NavLink 
            as="a"
            href="https://cal.com/scott-laughlin-i6yml3/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            scrolled={scrolled}
          >
            Book Consultation
          </NavLink>
        </NavLinks>
        
        <MobileMenuButton scrolled={scrolled} onClick={toggleMobileMenu}>
          ☰
        </MobileMenuButton>
      </NavContainer>
      
      {mobileMenuOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={toggleMobileMenu}
        />
      )}
      
      <AnimatePresence>
        <MobileMenu
          initial={{ x: '100%' }}
          animate={{ x: mobileMenuOpen ? 0 : '100%' }}
          exit={{ x: '100%' }}
          transition={{ type: 'tween' }}
        >
          <CloseButton onClick={toggleMobileMenu}>✕</CloseButton>
          <MobileLogoContainer>
            <MobileLogoText>
              <span className="tech">Tech</span>
              <span className="ramp">Ramp</span>
            </MobileLogoText>
          </MobileLogoContainer>
          <MobileNavLink to="process" smooth={true} duration={500} onClick={toggleMobileMenu}>How It Works</MobileNavLink>
          <MobileNavLink to="testimonials" smooth={true} duration={500} onClick={toggleMobileMenu}>Testimonials</MobileNavLink>
          <MobileNavLink 
            as="a"
            href="https://cal.com/scott-laughlin-i6yml3/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            onClick={toggleMobileMenu}
          >
            Book Consultation
          </MobileNavLink>
        </MobileMenu>
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header;
